/**
 * Blame stamping for policy-subcollection writes.
 *
 * Every write to `organizations/{orgKey}/policies/{policyKey}` must record WHO
 * performed it, so that `modifiedBy` is a trustworthy audit field and the Phase
 * B rules clause (`request.resource.data.modifiedBy == request.auth.uid`) can
 * be enforced. See docs/categorized/security/POLICIES-SECURITY-HARDENING-DESIGN.md §1.1.
 *
 * Keeping this in one helper is what stops the write sites drifting apart again
 * — before it, 12 of 13 sites either omitted blame or (on the main save path)
 * stamped the ORGANISATION KEY into `modifiedBy`.
 *
 * @param payload  the write payload; mutated in place and returned, so it can
 *                 be inlined directly in a `.update(...)` / `.set(...)` call
 * @param uid      the acting user's uid (`(await auth.currentUser)?.uid`)
 * @param options  `create: true` also stamps `createdBy`
 */
export function stampBlame<T extends Record<string, any>>(
    payload: T,
    uid: string | null | undefined,
    options: { create?: boolean } = {}
): T {
    // No authenticated user — record nothing rather than a placeholder that
    // would read as a real actor. Such a write is denied by the rules anyway.
    if (!uid) return payload;

    // Widened because TS will not let a generic type parameter be indexed for
    // writing; the payload is a plain Firestore write object either way.
    const target = payload as Record<string, any>;
    if (options.create) {
        target['createdBy'] = uid;
    }
    target['modifiedBy'] = uid;
    return payload;
}
